import { useEffect, useMemo, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useVisit } from "../../../contexts/VisitContext";
import { useCase } from "../../../contexts/CaseContext";
import { useAccount } from "../../../contexts/AccountContext";
import { useCreateVisitDialog } from "../../../providers/CreateVisitDialogProvider";
import { ReportService } from "../../../services/ReportService";
import { Report } from "@visitproof/types";
import moment from "moment";

export const useVisitDetail = () => {
  const { visitId } = useParams<{ visitId: string }>();
  const { cases } = useCase();
  const { account } = useAccount();
  const { openDialog } = useCreateVisitDialog();
  const { selectedVisit, setSelectedVisit, loading, error } = useVisit();
  const [visitReport, setVisitReport] = useState<Report | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Memoized case lookup
  const caseInfo = useMemo(() => {
    if (!selectedVisit?.caseId || !cases.length) return null;
    return cases.find((c) => c.id === selectedVisit.caseId) ?? null;
  }, [selectedVisit?.caseId, cases]);

  // Memoized duration calculation
  const duration = useMemo(() => {
    if (!selectedVisit?.startDate || !selectedVisit?.endDate) {
      return moment.duration(0);
    }
    return moment.duration(
      selectedVisit.endDate.toDate().getTime() -
        selectedVisit.startDate.toDate().getTime(),
    );
  }, [selectedVisit?.startDate, selectedVisit?.endDate]);

  // Callback for setting selected visit
  const handleSetSelectedVisit = useCallback((id: string) => {
    setSelectedVisit(id);
  }, [setSelectedVisit]);

  // Callback for duplicating visit
  const handleDuplicateVisit = useCallback(() => {
    if (!selectedVisit) return;
    
    openDialog({
      title: "Duplicate Visit",
      visit: { ...selectedVisit, id: undefined },
      onVisitCreated: (newVisitId) => {
        setSelectedVisit(newVisitId);
      },
    });
  }, [selectedVisit, openDialog, setSelectedVisit]);

  // Fetch report for this visit
  useEffect(() => {
    if (!visitId || !account?.id) return;

    const fetchVisitReport = async () => {
      setLoadingReport(true);
      try {
        const reports = await ReportService.getReportsByVisitId(visitId, account.id);
        setVisitReport(reports.length > 0 ? reports[0] : null);
      } catch (error) {
        console.error("Error fetching visit report:", error);
        setVisitReport(null);
      } finally {
        setLoadingReport(false);
      }
    };

    fetchVisitReport();
  }, [visitId, account?.id]);

  useEffect(() => {
    if (visitId) {
      handleSetSelectedVisit(visitId);
    }
  }, [visitId, handleSetSelectedVisit]);

  return {
    selectedVisit,
    loading,
    error,
    caseInfo,
    duration,
    visitId,
    visitReport,
    loadingReport,
    handleDuplicateVisit,
  };
}; 